// Child runs as a tree in the Analyses list (RESTYLE §5). The rule, as FAMILY_RULE says it on the page:
//   · the list is newest first; a child run (parent_job_id) sits directly under its parent, children in the order
//     they were submitted, and the family takes the parent's place and day group;
//   · filters and search match runs one by one: a matching child hangs under its parent only when the parent is shown
//     too, otherwise it stands at its own time with "child of …", as it does when the parent is not loaded;
//   · a folded parent keeps its children's count ("2 children"; "1 of 2 children" when filters hide some).
//   familyIndex(rows) → { kids, parentOf }      loaded children per parent (submission order), each child's loaded parent;
//                                               a link that would close a loop is dropped
//   arrange(rows, match, folded) → { groups, visible, matched }
//     groups   UTC day groups of TreeItems in display order (rows under a folded parent are left out; counts include them)
//     visible  the rows on screen, in order (j / k) · matched: how many rows pass the filters
import { dayOf, type Eff } from "./listState";
import { toMs } from "@/lib/format";

export const FAMILY_RULE =
  "A child run sits under its parent, in submission order, in the parent's place. Filters match runs one by one; a matching child whose parent is filtered out or not loaded stands alone with its parent's name.";

type TreeItem = {
  eff: Eff;
  /** 0 for a row that stands alone; n for a row n levels under a shown ancestor */
  depth: number;
  /** the parent it hangs under (shown above it, in this family) */
  under?: string;
  /** parent_job_id of a child that stands alone (its parent is not loaded or does not pass the filters) */
  orphanOf?: string;
  /** loaded children, and how many of them pass the filters (and of those, are live or malicious) */
  kids: number;
  shownKids: number;
  liveKids: number;
  maliciousKids: number;
  /** its children are folded away */
  folded: boolean;
  /** last of its shown siblings: its guide ends here */
  last: boolean;
  /** one char per ancestor level 1..depth−1: "1" when that ancestor's guide continues past this row */
  rails: string;
  /** the shown children's ids (aria-controls of the fold toggle) */
  childIds: readonly string[];
};

export type TreeGroup = { key: string; label: string; count: number; items: TreeItem[] };

const at = (e: Eff) => {
  const t = toMs(e.job.submitted_at);
  return Number.isFinite(t) ? t : Infinity;
};

export function familyIndex(rows: readonly Eff[]): { kids: Map<string, Eff[]>; parentOf: Map<string, string> } {
  const byId = new Map(rows.map((e) => [e.id, e]));
  const parentOf = new Map<string, string>();
  for (const e of rows) {
    const p = e.job.parent_job_id;
    if (p && p !== e.id && byId.has(p)) parentOf.set(e.id, p);
  }
  for (const e of rows) {
    const chain = new Set([e.id]);
    for (let p = parentOf.get(e.id); p; p = parentOf.get(p)) {
      if (chain.has(p)) {
        parentOf.delete(e.id);
        break;
      }
      chain.add(p);
    }
  }
  const kids = new Map<string, Eff[]>();
  for (const e of rows) {
    const p = parentOf.get(e.id);
    if (!p) continue;
    const list = kids.get(p) ?? [];
    list.push(e);
    kids.set(p, list);
  }
  for (const list of kids.values()) list.sort((a, b) => at(a) - at(b) || (a.id < b.id ? -1 : 1));
  return { kids, parentOf };
}

export function arrange(rows: readonly Eff[], match: (e: Eff) => boolean, folded: ReadonlySet<string>) {
  const { kids, parentOf } = familyIndex(rows);
  const ok = new Set<string>();
  for (const e of rows) if (match(e)) ok.add(e.id);

  const groups: TreeGroup[] = [];
  const visible: Eff[] = [];

  const place = (e: Eff, group: TreeGroup, depth: number, rails: string, last: boolean, hidden: boolean, under?: string) => {
    const shown = (kids.get(e.id) ?? []).filter((k) => ok.has(k.id));
    const fold = shown.length > 0 && folded.has(e.id);
    group.count++;
    if (!hidden) {
      group.items.push({
        eff: e,
        depth,
        under,
        orphanOf: under ? undefined : e.job.parent_job_id || undefined,
        kids: kids.get(e.id)?.length ?? 0,
        shownKids: shown.length,
        liveKids: shown.filter((k) => k.live && k.bucket === "running").length,
        maliciousKids: shown.filter((k) => k.bucket === "completed" && k.verdict?.label === "malicious").length,
        folded: fold,
        last,
        rails,
        childIds: shown.map((k) => k.id),
      });
      visible.push(e);
    }
    const childRails = depth === 0 ? "" : rails + (last ? "0" : "1");
    shown.forEach((k, i) => place(k, group, depth + 1, childRails, i === shown.length - 1, hidden || fold, e.id));
  };

  for (const e of rows) {
    if (!ok.has(e.id)) continue;
    const p = parentOf.get(e.id);
    if (p && ok.has(p)) continue; // it hangs under its parent
    const d = dayOf(e);
    let g = groups[groups.length - 1];
    if (!g || g.key !== d.key) {
      g = { ...d, count: 0, items: [] };
      groups.push(g);
    }
    place(e, g, 0, "", true, false);
  }
  return { groups, visible, matched: ok.size };
}
